/**
 * TeamSyncAdmin.js
 * 
 * Admin-Komponente zur Synchronisation von Team-Dokumenten zwischen
 * Realtime Database und Firestore.
 */

import React, { useState, useEffect } from 'react';
import { functions, auth } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import './TeamSyncAdmin.css';

const TeamSyncAdmin = () => {
  const [teamOwnerId, setTeamOwnerId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [singleMemberLoading, setSingleMemberLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [teamOwnersList, setTeamOwnersList] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Prüfe ob aktueller User Admin ist (via Custom Claims)
  useEffect(() => {
    const checkAdmin = async () => {
      if (auth.currentUser) {
        try {
          const tokenResult = await auth.currentUser.getIdTokenResult();
          setIsAdmin(tokenResult.claims.admin === true);
        } catch (err) {
          setIsAdmin(false);
        }
      }
    };
    checkAdmin();
  }, []);

  if (!isAdmin) {
    return (
      <div className="team-sync-admin-container">
        <div className="team-sync-admin-error">
          <h3>Access Denied</h3>
          <p>You need admin permissions to access this page.</p>
          <p>Current user: {auth.currentUser?.email || 'Not logged in'}</p>
        </div>
      </div>
    );
  }

  const handleSync = async () => {
    if (!teamOwnerId.trim()) {
      setError('Please enter a Team Owner ID');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const syncTeamDocument = httpsCallable(functions, 'syncTeamDocument');
      const response = await syncTeamDocument({ teamOwnerId: teamOwnerId.trim() });
      setResult(response.data);
      console.log('Sync Result:', response.data);
    } catch (err) {
      console.error('Sync Error:', err);
      setError(err.message || 'Failed to sync team document');
    } finally {
      setLoading(false);
    }
  };

  const handleDebug = async () => {
    if (!teamOwnerId.trim()) {
      setError('Please enter a Team Owner ID');
      return;
    }

    setDebugLoading(true);
    setError(null);
    setResult(null);

    try {
      const debugTeamMemberships = httpsCallable(functions, 'debugTeamMemberships');
      const response = await debugTeamMemberships({ teamOwnerId: teamOwnerId.trim() });
      setResult(response.data);
      console.log('Debug Result:', response.data);
    } catch (err) {
      console.error('Debug Error:', err);
      setError(err.message || 'Failed to debug team');
    } finally {
      setDebugLoading(false);
    }
  };

  const handleQuickSync = async (id, name) => {
    setTeamOwnerId(id);
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const syncTeamDocument = httpsCallable(functions, 'syncTeamDocument');
      const response = await syncTeamDocument({ teamOwnerId: id });
      setResult(response.data);
      console.log(`Sync Result for ${name}:`, response.data);
    } catch (err) {
      console.error('Sync Error:', err);
      setError(err.message || 'Failed to sync team document');
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateAll = async () => {
    if (!window.confirm('⚠️ ACHTUNG!\n\nDies wird ALLE Team-Dokumente migrieren.\nDas kann einige Sekunden dauern.\n\nFortfahren?')) {
      return;
    }

    setMigrateLoading(true);
    setError(null);
    setResult(null);

    try {
      const migrateAllTeams = httpsCallable(functions, 'migrateAllTeams', { timeout: 540000 });
      console.log('🚀 Starting migration of ALL teams...');
      const response = await migrateAllTeams({});
      setResult(response.data);
      console.log('Migration Result:', response.data);
    } catch (err) {
      console.error('Migration Error:', err);
      setError(err.message || 'Failed to migrate all teams');
    } finally {
      setMigrateLoading(false);
    }
  };

  // 🔍 List all team owners in the system
  const handleListAllTeamOwners = async () => {
    setListLoading(true);
    setError(null);
    setTeamOwnersList(null);
    setResult(null);

    try {
      const listAllTeamOwners = httpsCallable(functions, 'listAllTeamOwners');
      const response = await listAllTeamOwners({});
      setTeamOwnersList(response.data);
      console.log('Team Owners List:', response.data);
    } catch (err) {
      console.error('List Team Owners Error:', err);
      setError(err.message || 'Failed to list team owners');
    } finally {
      setListLoading(false);
    }
  };

  // Quick select a team owner from the list
  const handleSelectTeamOwner = (ownerId) => {
    setTeamOwnerId(ownerId);
    setMemberId('');
  };

  // 🔧 NEW: Sync single team member
  const handleSyncSingleMember = async () => {
    if (!teamOwnerId.trim()) {
      setError('Please enter a Team Owner ID');
      return;
    }
    if (!memberId.trim()) {
      setError('Please enter a Member ID');
      return;
    }

    setSingleMemberLoading(true);
    setError(null);
    setResult(null);

    try {
      const syncSingleTeamMember = httpsCallable(functions, 'syncSingleTeamMember');
      const response = await syncSingleTeamMember({ 
        teamOwnerId: teamOwnerId.trim(),
        memberId: memberId.trim()
      });
      setResult(response.data);
      console.log('Single Member Sync Result:', response.data);
    } catch (err) {
      console.error('Single Member Sync Error:', err);
      setError(err.message || 'Failed to sync team member');
    } finally {
      setSingleMemberLoading(false);
    }
  };

  // Quick sync for a single member from debug results
  const handleQuickSyncMember = async (ownerIdParam, memberIdParam, memberEmail) => {
    setSingleMemberLoading(true);
    setError(null);
    setResult(null);

    try {
      const syncSingleTeamMember = httpsCallable(functions, 'syncSingleTeamMember');
      const response = await syncSingleTeamMember({ 
        teamOwnerId: ownerIdParam,
        memberId: memberIdParam
      });
      setResult(response.data);
      console.log(`Single Member Sync Result for ${memberEmail}:`, response.data);
    } catch (err) {
      console.error('Single Member Sync Error:', err);
      setError(err.message || 'Failed to sync team member');
    } finally {
      setSingleMemberLoading(false);
    }
  };

  // 🔄 Upgrade all members to All Access
  const handleUpgradeAllToAllAccess = async () => {
    if (!window.confirm('⚠️ ACHTUNG!\n\nDies wird ALLE Team-Mitglieder auf "All Access" (Master) umstellen.\nDie "Leads Only" Option wird für alle entfernt.\n\nFortfahren?')) {
      return;
    }

    setUpgradeLoading(true);
    setError(null);
    setResult(null);

    try {
      const upgradeAllMembersToAllAccess = httpsCallable(functions, 'upgradeAllMembersToAllAccess', { timeout: 540000 });
      console.log('🔄 Starting upgrade of ALL members to All Access...');
      const response = await upgradeAllMembersToAllAccess({});
      setResult(response.data);
      console.log('Upgrade Result:', response.data);
    } catch (err) {
      console.error('Upgrade Error:', err);
      setError(err.message || 'Failed to upgrade members');
    } finally {
      setUpgradeLoading(false);
    }
  };

  return (
    <div className="team-sync-admin-container">
      <div className="team-sync-admin-header">
        <h2>🔧 Team Sync Admin</h2>
        <p>Synchronize Firestore team documents from Realtime Database</p>
      </div>

      {/* 🔍 List All Team Owners Section */}
      <div className="team-sync-admin-list-owners">
        <h3>🔍 Find All Team Owners</h3>
        <p>Scan the database to find all users who have team members</p>
        <button 
          onClick={handleListAllTeamOwners}
          disabled={listLoading || loading}
          className="team-sync-list-btn"
        >
          {listLoading ? '🔄 Scanning...' : '🔍 List All Team Owners'}
        </button>
      </div>

      {/* Team Owners List Results */}
      {teamOwnersList && teamOwnersList.teamOwners && (
        <div className="team-sync-owners-list">
          <h3>📋 Found {teamOwnersList.totalTeamOwners} Team Owners</h3>
          <div className="owners-grid">
            {teamOwnersList.teamOwners.map((owner) => (
              <div 
                key={owner.ownerId} 
                className={`owner-card ${owner.needsSync ? 'needs-sync' : 'synced'}`}
              >
                <div className="owner-header">
                  <span className="owner-email">{owner.ownerEmail}</span>
                  {owner.needsSync && <span className="sync-badge">⚠️ Needs Sync</span>}
                  {!owner.needsSync && <span className="sync-badge synced">✅ OK</span>}
                </div>
                <div className="owner-name">{owner.ownerName}</div>
                <div className="owner-id">{owner.ownerId}</div>
                <div className="owner-stats">
                  <span>Members: {owner.realtimeMembersCount}</span>
                  <span>Firestore: {owner.firestoreDocExists ? `${owner.firestoreMembersCount} members` : '❌ No doc'}</span>
                </div>
                
                {/* Members list */}
                <div className="owner-members">
                  <strong>Team Members:</strong>
                  {owner.members.map((member) => (
                    <div key={member.memberId} className="owner-member-item">
                      <span className="member-email">{member.memberEmail}</span>
                      <span className={`member-status ${member.status === 'pending' ? 'pending' : ''}`}>
                        {member.status}
                      </span>
                      <button
                        className="sync-member-inline-btn"
                        onClick={() => handleQuickSyncMember(owner.ownerId, member.memberId, member.memberEmail)}
                        disabled={singleMemberLoading}
                      >
                        🔧 Fix
                      </button>
                    </div>
                  ))}
                </div>

                <div className="owner-actions">
                  <button
                    className="select-owner-btn"
                    onClick={() => handleSelectTeamOwner(owner.ownerId)}
                  >
                    Select
                  </button>
                  <button
                    className="sync-team-btn"
                    onClick={() => handleQuickSync(owner.ownerId, owner.ownerEmail)}
                    disabled={loading}
                  >
                    🔄 Sync All
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upgrade All to All Access Section */}
      <div className="team-sync-admin-upgrade-all">
        <h3>⭐ Upgrade All Members to All Access</h3>
        <p>This will upgrade ALL team members from "Leads Only" to "All Access" (Master).</p>
        <button 
          onClick={handleUpgradeAllToAllAccess}
          disabled={loading || debugLoading || migrateLoading || upgradeLoading}
          className="team-sync-upgrade-all-btn"
        >
          {upgradeLoading ? '🔄 Upgrading... Please wait' : '⭐ Upgrade All to All Access'}
        </button>
      </div>

      {/* Migrate All Section */}
      <div className="team-sync-admin-migrate-all">
        <h3>🚀 Migrate All Teams</h3>
        <p>This will sync ALL team documents at once. Use this for initial migration.</p>
        <button 
          onClick={handleMigrateAll}
          disabled={loading || debugLoading || migrateLoading || upgradeLoading}
          className="team-sync-migrate-all-btn"
        >
          {migrateLoading ? '🔄 Migrating... Please wait' : '🚀 Migrate All Teams'}
        </button>
      </div>

      {/* Quick Sync Buttons für bekannte Teams */}
      <div className="team-sync-admin-quick">
        <h3>Quick Sync</h3>
        <div className="team-sync-admin-quick-buttons">
          <button 
            onClick={() => handleQuickSync('fT37eJA3Ehea9X6ABltzNDsxpjB2', 'Fox Performance')}
            disabled={loading || debugLoading}
            className="team-sync-quick-btn"
          >
            🦊 Fox Performance
          </button>
          <button 
            onClick={() => handleQuickSync('OvUkG0rKWpgTMoNUFSkmgvLujxq1', 'Generaxion Projects')}
            disabled={loading || debugLoading}
            className="team-sync-quick-btn"
          >
            🏢 Generaxion Projects
          </button>
        </div>
      </div>

      {/* Single Member Sync - NEW */}
      <div className="team-sync-admin-form team-sync-admin-single-member">
        <h3>👤 Sync Single Member</h3>
        <p className="form-description">Fix permissions for an individual team member</p>
        
        <div className="team-sync-admin-input-row">
          <div className="team-sync-admin-input-group">
            <label>Team Owner ID</label>
            <input
              type="text"
              value={teamOwnerId}
              onChange={(e) => setTeamOwnerId(e.target.value)}
              placeholder="Owner's User ID..."
              disabled={loading || debugLoading || singleMemberLoading}
            />
          </div>
          <div className="team-sync-admin-input-group">
            <label>Member ID</label>
            <input
              type="text"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              placeholder="Member's User ID..."
              disabled={loading || debugLoading || singleMemberLoading}
            />
          </div>
        </div>

        <div className="team-sync-admin-buttons">
          <button 
            onClick={handleSyncSingleMember}
            disabled={loading || debugLoading || singleMemberLoading || !teamOwnerId.trim() || !memberId.trim()}
            className="team-sync-btn team-sync-btn-single"
          >
            {singleMemberLoading ? '🔄 Syncing Member...' : '👤 Sync This Member'}
          </button>
        </div>
      </div>

      {/* Manual Input */}
      <div className="team-sync-admin-form">
        <h3>🔧 Manual Team Sync</h3>
        <div className="team-sync-admin-input-group">
          <label>Team Owner ID</label>
          <input
            type="text"
            value={teamOwnerId}
            onChange={(e) => setTeamOwnerId(e.target.value)}
            placeholder="Enter Team Owner User ID..."
            disabled={loading || debugLoading || singleMemberLoading}
          />
        </div>

        <div className="team-sync-admin-buttons">
          <button 
            onClick={handleDebug}
            disabled={loading || debugLoading || singleMemberLoading || !teamOwnerId.trim()}
            className="team-sync-btn team-sync-btn-debug"
          >
            {debugLoading ? '🔄 Loading...' : '🔍 Debug (Read Only)'}
          </button>
          <button 
            onClick={handleSync}
            disabled={loading || debugLoading || singleMemberLoading || !teamOwnerId.trim()}
            className="team-sync-btn team-sync-btn-sync"
          >
            {loading ? '🔄 Syncing...' : '🔄 Sync Entire Team'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="team-sync-admin-error">
          <h4>❌ Error</h4>
          <p>{error}</p>
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div className={`team-sync-admin-result ${result.success ? 'success' : 'error'}`}>
          <h4>{result.success ? '✅ Success' : '❌ Failed'}</h4>
          
          {result.message && <p className="result-message">{result.message}</p>}
          
          {result.foundMembers && (
            <div className="result-section">
              <h5>Found Members ({result.foundMembers.length})</h5>
              <ul>
                {result.foundMembers.map((member, index) => (
                  <li key={index}>
                    <strong>{member.email}</strong>
                    <span className="member-id">{member.id}</span>
                    <span className="member-type">{member.memberType}</span>
                    <span className="member-status">{member.status}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.realtimeDB && (
            <div className="result-section">
              <h5>Realtime Database</h5>
              <p>Members found: {result.realtimeDB.membersFound}</p>
              {result.realtimeDB.members && result.realtimeDB.members.length > 0 && (
                <ul className="members-list-with-actions">
                  {result.realtimeDB.members.map((member, index) => (
                    <li key={index} className="member-item-with-action">
                      <div className="member-info-row">
                        <strong>{member.email}</strong>
                        <span className="member-id">{member.userId}</span>
                        {member.membership && (
                          <span className={`member-status ${member.membership.status === 'pending' ? 'status-pending' : ''}`}>
                            Status: {member.membership.status || 'legacy'}
                          </span>
                        )}
                      </div>
                      <button
                        className="sync-member-btn"
                        onClick={() => handleQuickSyncMember(result.teamOwnerId, member.userId, member.email)}
                        disabled={singleMemberLoading}
                      >
                        {singleMemberLoading ? '...' : '🔄 Sync'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {result.firestore && (
            <div className="result-section">
              <h5>Firestore</h5>
              <p>Document exists: {result.firestore.documentExists ? '✅ Yes' : '❌ No'}</p>
              {result.firestore.data && result.firestore.data.members && (
                <>
                  <p>Members in document: {Object.keys(result.firestore.data.members).length}</p>
                  <ul>
                    {Object.entries(result.firestore.data.members).map(([uid, data]) => (
                      <li key={uid}>
                        <strong>{data.email}</strong>
                        <span className="member-id">{uid}</span>
                        <span className="member-type">{data.role}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {/* Raw JSON */}
          <details className="result-raw">
            <summary>Raw JSON Response</summary>
            <pre>{JSON.stringify(result, null, 2)}</pre>
          </details>
        </div>
      )}

      {/* Instructions */}
      <div className="team-sync-admin-instructions">
        <h3>📖 Instructions</h3>
        <ol>
          <li><strong>Debug:</strong> Read-only - shows what's in Realtime DB and Firestore</li>
          <li><strong>Sync:</strong> Creates/updates the Firestore team document from Realtime DB data</li>
          <li>After syncing, team members should be able to access the team's World data</li>
        </ol>
        <p className="info-text">
          The Team Owner ID is the Firebase User ID of the account owner (the person who invited team members).
        </p>
      </div>
    </div>
  );
};

export default TeamSyncAdmin;
